/**
 * Pawkit pet profiles.
 *
 * Stores a shopper's pet details in localStorage so product pages can surface
 * relevant size guidance. Purely client-side — no pet data ever leaves the
 * browser or is sent to the store.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pawkit:pet-profile';
  var LEGACY_KEY = 'pawkit_pet';

  function readStorage() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem(LEGACY_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function writeStorage(profile) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      window.localStorage.removeItem(LEGACY_KEY);
      return true;
    } catch (err) {
      return false;
    }
  }

  function clearStorage() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_KEY);
    } catch (err) {
      /* storage unavailable; nothing to clear */
    }
  }

  var SIZE_ALIASES = {
    xs: 'Small',
    s: 'Small',
    small: 'Small',
    petite: 'Small',
    m: 'Medium',
    medium: 'Medium',
    regular: 'Medium',
    l: 'Large',
    large: 'Large',
    xl: 'Large',
    xxl: 'Giant',
    giant: 'Giant',
    huge: 'Giant'
  };

  function normalizeSize(value) {
    if (!value) return '';
    var key = String(value).trim().toLowerCase();
    if (SIZE_ALIASES[key]) return SIZE_ALIASES[key];
    return String(value).trim();
  }

  function normalize(profile) {
    return {
      name: String(profile.name || '').trim().slice(0, 60),
      species: String(profile.species || '').trim().slice(0, 40),
      breed: String(profile.breed || '').trim().slice(0, 60),
      size: normalizeSize(profile.size).slice(0, 20),
      weight: String(profile.weight || '').trim().slice(0, 20),
      birthday: String(profile.birthday || '').trim().slice(0, 10)
    };
  }

  function isUsable(profile) {
    if (!profile) return false;
    return Boolean(profile.name || profile.species || profile.size);
  }

  var listeners = [];

  function emit(profile) {
    listeners.forEach(function (fn) {
      try {
        fn(profile);
      } catch (err) {
        /* a single bad listener must not break the others */
      }
    });
  }

  var PawkitPets = {
    get: function () {
      var profile = readStorage();
      if (!isUsable(profile)) return null;
      return normalize(profile);
    },

    save: function (profile) {
      var clean = normalize(profile);
      var ok = writeStorage(clean);
      emit(ok ? clean : null);
      return ok;
    },

    clear: function () {
      clearStorage();
      emit(null);
    },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      fn(PawkitPets.get());
      return function () {
        listeners = listeners.filter(function (item) {
          return item !== fn;
        });
      };
    },

    /**
     * Score how well a product fits the saved pet. Products can declare
     * target sizes via a comma-separated `data-pet-sizes` attribute.
     * Returns 'match', 'possible', or 'unknown' when no profile exists.
     */
    fitFor: function (element) {
      var profile = PawkitPets.get();
      if (!profile || !profile.size || !element) return 'unknown';

      var declared = element.getAttribute('data-pet-sizes');
      if (!declared) return 'unknown';

      var targets = declared
        .split(',')
        .map(function (part) {
          return normalizeSize(part);
        })
        .filter(Boolean);

      if (!targets.length) return 'unknown';

      var wanted = normalizeSize(profile.size);
      var exact = targets.some(function (target) {
        return target.toLowerCase() === wanted.toLowerCase();
      });
      if (exact) return 'match';

      var sizeOrder = ['Small', 'Medium', 'Large', 'Giant'];
      var wantedIndex = sizeOrder.indexOf(wanted);
      if (wantedIndex === -1) return 'unknown';

      var close = targets.some(function (target) {
        var targetIndex = sizeOrder.indexOf(target);
        return targetIndex !== -1 && Math.abs(targetIndex - wantedIndex) === 1;
      });
      return close ? 'possible' : 'no-match';
    }
  };

  window.PawkitPets = PawkitPets;

  function fillForm(form, profile) {
    if (!form) return;
    ['name', 'species', 'breed', 'size', 'weight', 'birthday'].forEach(function (field) {
      var input = form.elements[field];
      if (input && profile && profile[field]) input.value = profile[field];
    });
  }

  function initForms() {
    document.querySelectorAll('[data-pet-form]').forEach(function (form) {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';

      var existing = PawkitPets.get();
      fillForm(form, existing);

      var status = form.querySelector('[data-pet-status]');
      var clearBtn = document.querySelector('[data-pet-clear]');

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var data = {};
        ['name', 'species', 'breed', 'size', 'weight', 'birthday'].forEach(function (field) {
          var input = form.elements[field];
          if (input) data[field] = input.value;
        });

        var ok = PawkitPets.save(data);
        if (status) {
          status.textContent = ok
            ? form.getAttribute('data-success') || 'Saved'
            : form.getAttribute('data-error') || 'Could not save';
          status.hidden = false;
        }
        if (ok && form.hasAttribute('data-close-on-save')) {
          var dialog = form.closest('dialog');
          if (dialog && typeof dialog.close === 'function') dialog.close();
        }
      });

      if (clearBtn && !clearBtn.dataset.ready) {
        clearBtn.dataset.ready = 'true';
        clearBtn.addEventListener('click', function () {
          PawkitPets.clear();
          form.reset();
          if (status) {
            status.textContent = clearBtn.getAttribute('data-cleared') || 'Removed';
            status.hidden = false;
          }
        });
      }
    });
  }

  function initRecommendations() {
    PawkitPets.subscribe(function (profile) {
      document.querySelectorAll('[data-pet-recommendation]').forEach(function (block) {
        var matches = block.querySelectorAll('[data-pet-sizes]');
        var anyMatch = false;

        matches.forEach(function (item) {
          var fit = PawkitPets.fitFor(item);
          if (fit === 'match') {
            item.hidden = false;
            anyMatch = true;
          } else if (fit === 'unknown') {
            item.hidden = false;
          } else {
            item.hidden = true;
          }
        });

        var empty = block.querySelector('[data-pet-recommendation-empty]');
        if (empty) empty.hidden = anyMatch || !profile || !profile.size;
      });

      document.querySelectorAll('[data-pet-name]').forEach(function (el) {
        el.textContent = profile && profile.name ? profile.name : '';
      });

      document.querySelectorAll('[data-pet-greeting]').forEach(function (el) {
        el.hidden = !profile;
      });

      document.querySelectorAll('[data-pet-empty-state]').forEach(function (el) {
        el.hidden = Boolean(profile);
      });

      document.querySelectorAll('[data-pet-fit-badge]').forEach(function (badge) {
        var target = badge.closest('[data-pet-sizes]') || badge.parentElement;
        var fit = PawkitPets.fitFor(target);
        badge.hidden = fit !== 'match';
      });
    });
  }

  function initDialogs() {
    document.querySelectorAll('[data-pet-open]').forEach(function (btn) {
      if (btn.dataset.ready) return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', function () {
        var dialog = document.getElementById(btn.getAttribute('data-pet-open'));
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') {
          fillForm(dialog.querySelector('[data-pet-form]'), PawkitPets.get());
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      });
    });

    document.querySelectorAll('[data-pet-dialog-close]').forEach(function (btn) {
      if (btn.dataset.ready) return;
      btn.dataset.ready = 'true';
      btn.addEventListener('click', function () {
        var dialog = btn.closest('dialog');
        if (!dialog) return;
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      });
    });
  }

  function init() {
    initForms();
    initRecommendations();
    initDialogs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
